/**
 * The dock's geometry. It sits in `geometry/` rather than `chatbox/` because the `agentChat` slice reads the
 * default width, and a store that imports from the view layer is a cycle waiting to happen. The rule is
 * checkable rather than a matter of taste: every import in this folder resolves to `./something` — nothing here
 * reaches out of it, in either direction (`grep -h '^import' geometry/*.ts`).
 *
 * The JS clamp and the CSS clamp below are deliberately the same formula: the drag commits through the first,
 * and the browser re-resolves the second at paint time on every viewport change.
 */

export const DOCK_MIN_WIDTH_PX = 320
export const DOCK_DEFAULT_WIDTH_PX = 392
/**
 * The ceiling is a FRACTION of the viewport, and nothing else. Not the viewport minus a fixed page reserve — an
 * absolute reserve collapsed the ceiling below the 320px floor on every 1280 or 1366 laptop, pinning the dock at
 * its floor with no travel in the handle at all. And no longer a 720px absolute cap either: that bound on every
 * display from ~1600 up, so an ultrawide screen got the same dock as a laptop while the fraction it was meant to
 * bound went unused. 1280 → 576, 1366 → 615, 1440 → 648, 1920 → 864, 2560 → 1152, 3440 → 1548.
 */
export const DOCK_MAX_VIEWPORT_FRACTION = 0.45
/** The width the app reserves for the dock. `:root` defaults it to 0px (modules/account-root/style.scss). */
export const DOCK_WIDTH_VAR = "--kelly-dock-w"
/**
 * Where the stage starts: full screen covers the stage, not the viewport, so the nav rail stays visible and the
 * active section stays legible. `:root` defaults it to 0px — a shell with no rail has no strip to
 * leave behind — and the shell that renders one raises it (`use-stage-left.ts`).
 */
export const STAGE_LEFT_VAR = "--kelly-stage-left"
/** `home-layout/style.scss`'s `.nav-bar { flex: 0 0 5rem }`. Anchored to the rail, never to the top chrome, whose height varies. */
export const NAV_RAIL_WIDTH = "5rem"

/**
 * The app bar's ROW, one height across every shell. Not the whole top chrome — some shells stack a 40px subnav.
 * - A second literal by necessity: SCSS owns it as `$nav-bar-height`, which TS cannot read.
 * - Guarded, not loose: `DockedBarSharesTheAppBarsBand` pins Kelly's bar bottom to home's.
 * - The hairline is a further 1px, carried on home's AppBar border — hence `content-box` at the call site.
 */
export const TOP_BAR_HEIGHT = "50px"

/**
 * The widest the dock may be on a given viewport. Never below the floor, so the bounds can never invert.
 * Rounds where CSS `45vw` does not (1366 → 615 here, 614.7 there). That is not a drift path and does not want
 * "fixing": the CSS clamp is the paint authority, this only bounds what a drag commits, and the two cannot
 * separate by more than a pixel.
 */
export function dockWidthCeiling(viewportWidth: number): number {
  return Math.max(DOCK_MIN_WIDTH_PX, Math.round(viewportWidth * DOCK_MAX_VIEWPORT_FRACTION))
}

/** Clamp a drag candidate. Called once per gesture with the viewport captured at pointer-down, never on an observation. */
export function clampDockWidth(width: number, viewportWidth: number): number {
  return Math.min(Math.max(Math.round(width), DOCK_MIN_WIDTH_PX), dockWidthCeiling(viewportWidth))
}

/**
 * The same bound as CSS. Both the dock's own box and the reservation custom property carry this string, so they
 * cannot drift, and a narrowed window re-clamps both for free — no ResizeObserver, and no monotonic JS re-clamp
 * that would strand the user's chosen width the first time the viewport narrowed and widened again.
 */
export function dockWidthCss(width: number): string {
  const ceiling = `max(${DOCK_MIN_WIDTH_PX}px, ${DOCK_MAX_VIEWPORT_FRACTION * 100}vw)`
  return `clamp(${DOCK_MIN_WIDTH_PX}px, ${width}px, ${ceiling})`
}
